/**
 * Descobre se a etapa da Jornada leva a uma tela que o escritório NÃO contratou.
 *
 * Antes disto, clicar numa etapa dessas navegava e o guard global do router respondia
 * `next({ name: '404' })` — o usuário caía numa página de erro sem entender por quê
 * (reportado pelo time do Fernando: "na etapa de referência aparece página não
 * encontrada"). Verificado no escritório 83: `Comissões Base de Dados` e `bi-embeded`
 * desligados derrubavam 4 etapas.
 *
 * A checagem aqui REPLICA a do guard (`src/router/index.js`) de propósito — mesma fonte
 * (`parameters/show` + `user/currentUser`), mesma ordem, mesmo tratamento de
 * `can_show` array/string e de `customPermission`. Se o guard mudar, este arquivo tem
 * que acompanhar; a alternativa (exportar a checagem do guard) mexeria no router, que é
 * caminho crítico de toda navegação do app.
 *
 * Por que resolver a URL em vez de anotar a flag no catálogo: o catálogo é servido pelo
 * backend, que não conhece as `meta` do vue-router. A URL é o único elo já existente
 * entre etapa e tela — e `router.resolve()` devolve exatamente a rota que o guard
 * avaliaria.
 */

/**
 * A CADEIA de registros casados (`route.matched`), do pai à folha.
 *
 * ★ Por que a cadeia e não `route.meta` (2026-08-05, bug em tela): no vue-router 3
 * `route.meta` é o meta do registro MAIS PROFUNDO, não a fusão pai+filho — e há rotas
 * cujo gate mora só no PAI. `/bi-embed` declara `can_show: BI_EMBED`, mas o filho
 * `/bi-embed/home` (`embed.home`) não tem `meta` nenhum. Lendo só o meta da folha, a
 * etapa passava; e como o módulo só some quando TODAS as suas etapas são bloqueadas,
 * UMA etapa passando mantinha a trilha inteira do BI na tela de quem não tem BI.
 * O mesmo padrão existe em comissões, com o gate em níveis intermediários.
 *
 * ⚠️ Isto torna o gate MAIS estrito que o guard global (`src/router/index.js`), que
 * lê `routeTo.meta` e portanto também deixa passar essas rotas. A divergência é
 * deliberada e na direção segura: aqui o custo de errar é esconder/mostrar item de
 * lista; lá, mudar o comportamento de toda navegação do app.
 */
const resolverCadeia = (router, url) => {
  if (!router || !url) return []
  try {
    const resolvido = router.resolve(url)
    const rota = (resolvido && resolvido.route) || null
    if (!rota) return []
    // `matched` é o caminho normal; o fallback cobre stub de teste e rota sem cadeia.
    if (Array.isArray(rota.matched) && rota.matched.length) return rota.matched
    return rota.meta ? [{ meta: rota.meta }] : []
  } catch (erro) {
    // URL que o router não resolve não é motivo para bloquear a etapa: deixa navegar e
    // o guard decide. Bloquear aqui esconderia a etapa por um erro nosso de catálogo.
    return []
  }
}

const temTodasAsFlags = (canShow, show) => {
  if (Array.isArray(canShow)) return canShow.every((flag) => !!show[flag])
  return !!show[canShow]
}

// ★ `meta.permission` — MAIS UMA dimensão do guard que produz 404
// (2026-08-07, reportado em tela pelo owner).
//
// `can_show` e `customPermission` fazem a etapa SUMIR. `permission` não era
// checado aqui, então a Jornada mostrava a etapa clicável e o guard do router
// respondia `/404` — no Financeiro, "Cadastrar Caixa e Endividamento manual" e
// "Baixar planilha da DRE / DFC", ambas em `/financeiro/demonstrativo`.
//
// ⚠️ Replica a regra do guard (`src/router/index.js:364-380`) INCLUSIVE a
// quirk que causa o 404, porque o trabalho deste gate é PREVER o guard — se
// divergir "para melhor", a etapa volta a aparecer e a levar a 404:
//
//   `store.getters['parameters/canShow']` é `(attr) => state.show[attr]`
//   (parameters/getters.js:58) — NÃO trata array. A rota do demonstrativo
//   declara `can_show: [BPO_FINANCE, P_L_PJ]`, então o guard indexa `show` por
//   um array virado string, recebe `undefined`, e `hasPermission` cai. Note que
//   `temTodasAsFlags` (acima) trata array certo — é justamente essa divergência
//   que fazia o gate liberar onde o guard barra.
const permissionBarra = (meta, user, show) => {
  const permission = meta.permission || []
  if (!permission.length) return false
  if ((user.tipo_usuario || '').toLowerCase() === 'gestor') return false
  if (permission.includes('all')) return false
  if (permission.includes(user.tipo_usuario)) return false
  if (permission.includes(user.role)) return false
  // Espelha `canShow` do guard: indexação direta, sem suporte a array.
  return meta.can_show ? !show[meta.can_show] : false
}

// ★ `meta.whitelabel_only` — dimensão que faltava (2026-08-10, achado de
// review do PR #1890). Réplica de `src/router/index.js:229-241`. Fonte do
// valor é `window.WHITELABEL` (global do browser, setado por
// `src/api/apiUserConfig.js:236` a partir de `data.white_label`), não Vuex —
// é por isso que esta checagem não recebe `show`/`user`.
const whitelabelBarra = (meta) =>
  !!meta.whitelabel_only &&
  !(globalThis.window && globalThis.window.WHITELABEL)

// ★ `meta.listCanShow` — dimensão que faltava. Réplica de
// `src/router/index.js:276-292`: semântica OR (basta UMA flag do array estar
// ligada), ao contrário de `can_show` array (`temTodasAsFlags`, semântica AND).
const listCanShowBarra = (meta, show) => {
  if (!Array.isArray(meta.listCanShow) || !meta.listCanShow.length) return false
  return !meta.listCanShow.some((flag) => !!show[flag])
}

// ★ `meta.only_aawz` (+ `meta.external_flag`) e `meta.internal` — dimensões
// que faltavam. Réplica de `src/router/index.js:294-317` e `:350-362`.
const onlyAawzBarra = (meta, user, show) => {
  if (!meta.only_aawz) return false
  const hasExternalFlagAccess = !!meta.external_flag && !!show[meta.external_flag]
  return !(user.IS_INTERNAL && user.id_interno) && !hasExternalFlagAccess
}

const internalBarra = (meta, user) => !!meta.internal && !user.IS_INTERNAL

// Réplica as MESMAS dimensões do guard (`src/router/index.js`): whitelabel_only
// (229), can_show (243), listCanShow (276), only_aawz+external_flag (294),
// internal (350), customPermission (330), permission (364) — cada `if` é
// independente (acumulação OR: qualquer um bloqueando já retorna `true`), então
// a ORDEM de avaliação não muda o resultado, só o custo (early-exit no primeiro
// que bloquear). `customPermission` NÃO faz early-return sobre `permission`: no
// guard os dois blocos são independentes — um `meta` com os dois precisa passar
// nos dois, senão a etapa escapa de um 404 que só um deles produziria.
const registroBarra = (registro, user, show) => {
  const meta = (registro && registro.meta) || {}
  if (whitelabelBarra(meta)) return true
  if (meta.can_show && !temTodasAsFlags(meta.can_show, show)) return true
  if (listCanShowBarra(meta, show)) return true
  if (onlyAawzBarra(meta, user, show)) return true
  if (internalBarra(meta, user)) return true
  if (typeof meta.customPermission === 'function' && !meta.customPermission({ user, show })) {
    return true
  }
  return permissionBarra(meta, user, show)
}

// Basta UM registro da cadeia barrar: o gate de um pai vale para toda a subárvore.
const cadeiaBarra = (cadeia, user, show) =>
  cadeia.some((registro) => registroBarra(registro, user, show))

// O MESMO usuário, promovido a gestor do próprio escritório. Não é simulação de
// login: é a pergunta "o gestor daqui conseguiria abrir esta tela?", respondida
// pelo mesmo predicado, com as MESMAS flags (`show` não muda — quem muda é só o
// papel). Os três campos são os que o guard e as `customPermission` leem.
const comoGestor = (user) => ({
  ...user,
  IS_GESTOR: true,
  tipo_usuario: 'gestor',
  role: 'gestor',
})

/**
 * Por que a etapa está barrada — `null` (não está), `'role'` ou `'flag'`.
 *
 * ★ Existe porque "não disponível" eram DOIS problemas com a mesma cara
 * (2026-08-07, pergunta do owner: *"faz sentido esconder? ou ela precisa saber
 * que tem que pedir para o gestor?"*). Medido na assessora do escritório de
 * teste: das 57 obrigatórias barradas, ~52 caem em
 * `permissionSettingsV2(ctx, true, ...)` ou `meta.permission: ['gestor']` — o
 * escritório TEM a funcionalidade (o gestor do mesmo escritório vê zero
 * barradas), só não é ela quem executa. Mostrá-las sob "Não disponíveis no seu
 * plano" dizia *comercial* onde a verdade é *papel*: ela lia "meu plano não
 * tem" e pedia upgrade de algo já contratado.
 *
 * **Como o motivo é descoberto sem parsear `customPermission`:** ela é uma
 * função opaca que devolve booleano. Em vez de inspecionar, roda-se o MESMO
 * predicado com o usuário promovido a gestor. Destravou ⇒ o que barra é o
 * PAPEL; continua barrado ⇒ falta recurso ao escritório. Funciona para as três
 * dimensões (`can_show`, `customPermission`, `permission`) sem conhecer nenhuma.
 *
 * Reavalia a cadeia inteira na segunda passada, não só o registro que barrou:
 * a pergunta é se o gestor abriria a TELA, e um pai pode barrar por flag mesmo
 * quando o filho barrava por papel.
 *
 * @returns {String|null} 'role' | 'flag' | null
 */
export const motivoDoBloqueio = ({ router, store, url } = {}) => {
  const cadeia = resolverCadeia(router, url)
  if (!cadeia.length || !store) return null

  const show = store.getters['parameters/show'] || {}
  const user = store.getters['user/currentUser'] || {}

  if (!cadeiaBarra(cadeia, user, show)) return null
  // Segunda passada só acontece para etapa JÁ barrada — o caminho feliz segue
  // com uma avaliação só.
  return cadeiaBarra(cadeia, comoGestor(user), show) ? 'flag' : 'role'
}

/**
 * @param {Object} params
 * @param {Object} params.router  instância do vue-router (this.$router)
 * @param {Object} params.store   store Vuex (this.$store)
 * @param {String} params.url     `task.url` do catálogo
 * @returns {Boolean} true quando a tela existe mas o usuário não tem acesso
 */
export const etapaBloqueadaPorContrato = (params) =>
  motivoDoBloqueio(params) !== null

// Fábrica do predicado usado para ESCONDER etapa/módulo não contratado.
//
// ★ Duas coisas que só existem porque o veredito passou de "desabilita" para
// "some da tela" (2026-08-05) — errar agora apaga o produto, não só trava uma
// linha:
//
// 1. **`show` vazio ⇒ NADA bloqueado.** `parameters/show` é carregado no
//    `beforeEach` do router, e o `GET /config-journey` do `created` pode
//    resolver ANTES disso. Com `show = {}` o fail-open de `resolverMeta` não
//    protege — `meta.can_show` existe, `!show[flag]` é true, e a jornada
//    inteira desapareceria por uma corrida. `show` vazio não é "escritório sem
//    nada contratado"; é "as flags ainda não chegaram".
// 2. **Memo por URL, na instância da fábrica.** `router.resolve()` percorre a
//    tabela de rotas inteira (481 registros hoje) e o catálogo tem ~184 etapas.
//    O cache fica na `Map` local, nunca em escopo de módulo — estado mutável
//    compartilhado exigiria um `limpar()` só para os testes.
// Memoiza o MOTIVO, não o booleano: `router.resolve()` percorre a tabela de
// rotas, e o motivo custa uma segunda passada na cadeia. Guardando o motivo, o
// gate booleano sai de graça dele — quem só quer saber "está barrada?" não paga
// nada a mais, e quem quer o motivo não resolve a rota de novo.
const criarGateDeMotivo = ({ router, store } = {}) => {
  if (!router || !store) return () => null
  const show = store.getters['parameters/show'] || {}
  if (!Object.keys(show).length) return () => null

  const memo = new Map()
  return (url) => {
    if (!url) return null
    if (memo.has(url)) return memo.get(url)
    const motivo = motivoDoBloqueio({ router, store, url })
    memo.set(url, motivo)
    return motivo
  }
}

export const criarGateDeContrato = (params) => {
  const motivo = criarGateDeMotivo(params)
  return (url) => motivo(url) !== null
}

// Açúcar com a assinatura `(task)` que `filtrarModulosVisiveis` e
// `proximaEtapaPendenteDoModulo` esperam.
export const criarPredicadoDeEtapaBloqueada = ({ router, store } = {}) => {
  const gate = criarGateDeContrato({ router, store })
  return (task) => gate(task && task.url)
}

// ★ VITRINE — a etapa que existe, aparece e não pode ser feita (2026-08-05).
//
// É o terceiro estado de uma etapa, e não se confunde com os dois que já havia:
//
//   contratada  → aparece e funciona
//   bloqueada   → SOME da tela (`criarGateDeContrato` acima)
//   vitrine     → aparece esmaecida e inerte
//
// Existe porque as duas etapas de IA do Fee Fixo são um recurso à parte, e o
// owner quer que o profissional saiba que elas existem ("deixa ele ler que
// existe") sem que elas travem a trilha. Esconder venderia mal; deixar clicável
// levaria a um 404 do guard.
//
// **Quem declara é o CATÁLOGO**, em `metadata.premium_feature`, e não o front:
// `ensaiar-conversa-roleplay` aponta para /fee-fixo/clientes, uma rota que NÃO é
// gateada por IA — deduzir a flag pela URL, como o gate de contrato faz, deixaria
// essa etapa passar como se fosse comum. A flag é dado da etapa, não da tela.
//
// `show` vazio ⇒ nada é vitrine, pela MESMA corrida documentada em
// `criarGateDeContrato`: `parameters/show` chega no `beforeEach` do router e o GET
// da jornada pode resolver antes. Marcar ali pintaria de cinza uma trilha inteira
// que o escritório tem.
export const criarPredicadoDeVitrine = ({ store } = {}) => {
  if (!store) return () => false
  const show = store.getters['parameters/show'] || {}
  if (!Object.keys(show).length) return () => false
  return (task) => {
    const metadata = (task && task.metadata) || {}
    const flag = metadata.premium_feature
    return !!flag && !show[flag]
  }
}
